<template>
	<div ref="refChart" class="kchart-container"></div>
</template>

<script setup>
import * as echarts from 'echarts/core';
import {
	ToolboxComponent,
	TooltipComponent,
	GridComponent,
	VisualMapComponent,
	LegendComponent,
	BrushComponent,
	DataZoomComponent
} from 'echarts/components';
import { CandlestickChart, LineChart, BarChart } from 'echarts/charts';
import { UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import { ref, watch, nextTick, getCurrentInstance } from 'vue';
import { convertNumberToStr } from '@/utils/api';
import { createLegendOptions } from '@/utils/echarts-options';
import { convertPX } from '@/utils/echarts';
import { EN } from '@/locales';
import { useChartResize } from '../useChartResize';
import { useKChartCommon } from './useKChartCommon';

echarts.use([
	ToolboxComponent,
	TooltipComponent,
	GridComponent,
	VisualMapComponent,
	LegendComponent,
	BrushComponent,
	DataZoomComponent,
	CandlestickChart,
	LineChart,
	BarChart,
	CanvasRenderer,
	UniversalTransition
]);

const props = defineProps({
	data: {
		type: Array,
		default: () => []
	},
	isShowDataZoom: {
		type: Boolean,
		default: true
	},
	dataZoomStartValue: {
		type: Number,
		default: 60
	},
	fullCode: {
		type: String,
		default: ''
	}
});

const { proxy } = getCurrentInstance();

const {
	isHKStock,
	upColor,
	downColor,
	splitData,
	calculateMA,
	formatEnUnit,
	createTooltipPosition,
	createTooltipFormatter
} = useKChartCommon(props);

const maxData = ref(0);
let chartInstance = null;
const refChart = ref();

const { startListening, stopListening } = useChartResize(() => chartInstance);

function formatGrid() {
	return [
		{
			left: '2%',
			right: '5%',
			height: '50%',
			top: '10%',
			containLabel: true
		},
		{
			left: '2%',
			right: '5%',
			height: '16%',
			top: '66%',
			containLabel: true
		}
	];
}

const intMaxData = data => {
	let start = data.values.length - props.dataZoomStartValue - 1;
	let end = data.values.length - 1;
	maxData.value = Math.max(...data.values.slice(start, end).map(item => item[6]));
};

const renderChart = () => {
	// 停止旧的监听
	stopListening();

	if (chartInstance) {
		chartInstance.dispose();
		chartInstance.clear();
		chartInstance = null;
	}

	if (!props.data || !props.data.length) return;

	// 确保数据按时间正序排列
	const arr = [...props.data].reverse();
	const data = splitData(arr);

	chartInstance = echarts.init(refChart.value);

	const option = {
		color: ['#fe9f26', '#3964e5'],
		animation: false,
		legend: {
			...createLegendOptions(),
			bottom: convertPX(30),
			left: 'center',
			data: [`${proxy.$t('enterprise.candle')}`, 'MA5', 'MA20']
		},
		tooltip: {
			trigger: 'axis',
			axisPointer: {
				type: 'cross'
			},
			triggerOn: 'mousemove',
			borderWidth: 1,
			borderColor: '#ccc',
			padding: convertPX(10),
			textStyle: {
				color: '#000'
			},
			position: createTooltipPosition(),
			formatter: createTooltipFormatter(data)
		},
		axisPointer: {
			link: [
				{
					xAxisIndex: 'all'
				}
			],
			label: {
				backgroundColor: '#777'
			}
		},
		visualMap: {
			show: false,
			seriesIndex: 3,
			dimension: 2,
			pieces: [
				{
					value: 1,
					color: downColor
				},
				{
					value: -1,
					color: upColor
				}
			]
		},
		grid: formatGrid(),
		xAxis: [
			{
				type: 'category',
				data: data.categoryData,
				boundaryGap: true,
				axisLine: {
					onZero: false,
					lineStyle: {
						color: '#E8EAEF'
					}
				},
				axisLabel: {
					show: true,
					color: '#989898',
					fontSize: convertPX(12)
				},
				splitLine: {
					show: false,
					lineStyle: {
						color: '#F3F3F3'
					}
				},
				min: 'dataMin',
				max: 'dataMax',
				axisPointer: {
					z: 100
				},
				axisTick: {
					show: true
				}
			},
			{
				type: 'category',
				gridIndex: 1,
				data: data.categoryData,
				boundaryGap: true,
				axisLine: {
					onZero: false,
					lineStyle: {
						color: '#E8EAEF'
					}
				},
				axisTick: { show: false },
				splitLine: {
					show: false,
					lineStyle: {
						color: '#F3F3F3'
					}
				},
				axisLabel: {
					show: false,
					color: '#4E5969'
				},
				min: 'dataMin',
				max: 'dataMax'
			}
		],
		yAxis: [
			{
				scale: true,
				splitLine: {
					lineStyle: { color: '#F2F3F5', type: 'dashed' }
				},
				axisLabel: {
					show: true,
					color: '#989898',
					fontSize: convertPX(12),
					inside: false
				},
				axisLine: {
					show: false,
					lineStyle: {
						color: '#F2F3F5'
					}
				},
				splitNumber: '',
				zlevel: 2
			},
			{
				scale: true,
				gridIndex: 1,
				splitNumber: 1,
				axisLabel: {
					show: true,
					color: '#989898',
					fontSize: convertPX(12),
					formatter(value, index) {
						if (index !== 0) {
							if (proxy.$i18n.locale === EN) return formatEnUnit(value);
							return convertNumberToStr(value) + `${isHKStock.value ? '股' : '手'}`;
						} else {
							return '';
						}
					},
					inside: false
				},
				axisLine: {
					show: true,
					lineStyle: {
						color: '#F2F3F5'
					}
				},
				axisTick: { show: false },
				splitLine: {
					show: false,
					lineStyle: { color: '#F2F3F5', type: 'solid' }
				},
				zlevel: 2
			}
		],
		dataZoom: [
			{
				type: 'inside',
				xAxisIndex: [0, 1],
				startValue: data.values.length - props.dataZoomStartValue - 1,
				endValue: data.values.length - 1,
				disabled: !props.isShowDataZoom
			},
			{
				show: props.isShowDataZoom,
				xAxisIndex: [0, 1],
				type: 'slider',
				backgroundColor: 'rgba(57, 100, 229, 0.1)',
				dataBackground: {
					lineStyle: {
						width: 1,
						color: 'rgba(57, 100, 229, 0.2)'
					},
					areaStyle: {
						color: 'rgba(57, 100, 229, 0.1)'
					}
				},
				fillerColor: 'rgba(57, 100, 229, 0.3)',
				selectedDataBackground: {
					lineStyle: {
						width: 1,
						color: 'rgba(57, 100, 229, 0.4)'
					},
					areaStyle: {
						color: 'rgba(57, 100, 229, 0.3)'
					}
				},
				borderColor: 'rgba(57, 100, 229, 0.1)',
				borderRadius: 0,
				handleIcon: 'M1 0 H3 A1 1 0 0 1 4 1 V15 A1 1 0 0 1 3 16 H1 A1 1 0 0 1 0 15 V1 A1 1 0 0 1 1 0 Z',
				handleStyle: {
					color: 'rgba(57, 100, 229, 1)'
				},
				height: convertPX(24),
				bottom: 0,
				textStyle: {
					color: 'rgba(57, 100, 229, 1)',
					fontFamily: 'Barlow',
					fontSize: convertPX(10),
					lineHeight: convertPX(16)
				},
				brushSelect: false
			}
		],
		series: [
			{
				name: `${proxy.$t('enterprise.candle')}`,
				type: 'candlestick',
				data: data.values,
				barMaxWidth: convertPX(20),
				itemStyle: {
					color: 'transparent',
					color0: downColor,
					borderColor: upColor,
					borderColor0: undefined
				}
			},
			{
				name: 'MA5',
				type: 'line',
				data: calculateMA(5, data),
				symbol: 'none',
				smooth: true,
				lineStyle: {
					opacity: 0.5
				}
			},
			{
				name: 'MA20',
				type: 'line',
				data: calculateMA(20, data),
				symbol: 'none',
				smooth: true,
				lineStyle: {
					opacity: 0.5
				}
			},
			{
				name: 'Volume',
				type: 'bar',
				data: data.volumes,
				xAxisIndex: 1,
				yAxisIndex: 1,
				barMaxWidth: convertPX(20)
			}
		]
	};

	chartInstance.setOption(option, true);
	chartInstance.dispatchAction({
		type: 'brush'
	});

	// 设置交易量的y轴最大值
	intMaxData(data);
	chartInstance.setOption(option);

	// 启动响应式监听
	startListening();
};

watch(
	() => props.data,
	() => {
		nextTick(() => {
			renderChart();
		});
	},
	{ immediate: true, deep: true }
);

defineExpose({
	refresh: renderChart
});
</script>

<style lang="less" scoped>
.kchart-container {
	width: 100%;
	height: 400px;
}
</style>
